import { MDialog, MSpinner } from "components/Shared";
import React, { useEffect, useState, useRef, useMemo } from "react";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { getCustomerList } from "requests/customer";
import { CustomerListResponse, EmployeeComplaintAddRequest, EmployeeComplaintCategory } from "interfaces/Mueller.Business.Models.ts";
import Button from "@mui/material/Button";
import { postComplaintAttachment, postComplaint, getComplaintCategories } from "requests/employeeComplaints";
import EmployeeComplaintFileManager from "./EmployeeComplaintFileManager.tsx";
import responseHandler from "responseHandler/index";
import { connect } from "react-redux";

interface Props {
  employeeId: number,
  visible: boolean,
  onCancel: () => void,
  onSubmit?: () => void,
  employeeName: string,
  //redux
  makeToast: (text: string, type: string) => void;
}

interface Attachment {
  attachmentData: File,
  fileName: string,
}

const EmployeeComplaintAddDialog = (props: Props) => {

  const emptyDto: EmployeeComplaintAddRequest = {
    employeeId: props.employeeId,
    complaintCategoryCode: "",
    description: "",
    controlIdNumber: null,
    customerNumber: null,
    attachments: [],
    surveyScore: null,
};

  const mounted = useRef<boolean>(true);

  const formControlMethods = useRef({});
  const [customerOptions, setCustomerOptions] = useState(null);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState<boolean>(true);
  const [newComplaint, setNewComplaint] = useState<EmployeeComplaintAddRequest>({...emptyDto});
  const [newComplaintAttachments, setNewComplaintAttachments] = useState([]);
  const [isAddingAttachments, setIsAddingAttachments] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [complaintCategories, setComplaintCategories] = useState<EmployeeComplaintCategory[]>([]);
  const [isFetchingCategories, setIsFetchingCategories] = useState<boolean>(true);

  useEffect(() => {
    //add employee id to the new complaint dto upon component mounting
    setNewComplaint({ ...newComplaint, employeeId: props.employeeId });

    //get list of customers to select for the complaint and format them for a dropdown
    getCustomerList((customers: CustomerListResponse[]) => {
      setCustomerOptions(customers.map((c) => {
        return { label: c.displayLabel, value: c.number };
      }).sort((a,b) => {
        return a.value - b.value;
      }));
      setIsFetchingCustomers(false);
    }, mounted);

    //get list of complaint categories
    getComplaintCategories(
      props.employeeId,
      mounted,
      (categories) => {
        setComplaintCategories(categories);
        setIsFetchingCategories(false);
      },
      () => {
        props.makeToast("Failed to fetch complaint categories. Please try again", "error");
      });

    return () => { mounted.current = false; };
  }, [props.employeeId]);

  const memoizedFormDef = useMemo(() => {
    const formDef = [
      [
        { dataType: DATA_TYPE_DROPDOWN2, label: "Feedback Type", source: "complaintCategoryCode", hasValidationError: validator.required, dataTypeOptions: { options: complaintCategories.map(c => ({ label: c.name, value: c.code })) } }
      ],
      [
        { dataType: DATA_TYPE_DROPDOWN2, label: "Customer", source: "customerNumber", dataTypeOptions: { options: customerOptions } }
      ],
      [
        { dataType: DATA_TYPE_NUMBER, label: "Control number", source: "controlIdNumber", hasValidationError: validator.combineValidators(validator.notRequired, validator.integer) } //If you don't put the notRequired function first, this doesn't work
      ],
      [
        { dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Details", source: "description", hasValidationError: validator.required, dataTypeOptions: { defaultLines: 6} }
      ],
    ];
    return formDef;
  }, [customerOptions, complaintCategories]);

  const submit = (callback) => {
    if (formControlMethods.current.isFormValid())
      callback();
  };

  const resetForm = () => {
    setNewComplaint({...emptyDto});
    setNewComplaintAttachments([]);
  };

  // Sent to submit function as callback
  const addComplaint = (complaint: EmployeeComplaintAddRequest) => {
    postComplaint(
      complaint,
      () => {
      if (props.onSubmit)
        props.onSubmit();
      resetForm();
      setIsSubmitting(false);
      },
      () => {
        props.makeToast("Failed to submit feedback. Please try again", "error");
        setIsSubmitting(false);
      }, 
      mounted);
  };

  // Async is needed here so that we only submit the complaint after all attachments have been posted
  const saveAttachmentAsync = async (attachment: Attachment) =>
    new Promise<string>((resolve, reject) => postComplaintAttachment(attachment.attachmentData, attachment.fileName, resolve, reject, mounted));

  const submitComplaint = async () => {
    //We're limiting the attachments to 10. Was requested not to throw a toast. Red text will appear if too many attachments exist before submitting
    if(newComplaintAttachments && newComplaintAttachments.length > 10)
      return;

    setIsSubmitting(true);

    let attachmentGuids: string[] = null;
    let complaint: EmployeeComplaintAddRequest = { ...newComplaint };
    
    try {
      attachmentGuids = await Promise.all(newComplaintAttachments.map(saveAttachmentAsync));
    } catch {
      props.makeToast("Attachments did not save successfully. Please try again to submit feedback", "error");
      setIsSubmitting(false);
      return;
    }

    if (attachmentGuids) {
      complaint = { ...newComplaint, attachments: attachmentGuids };
      addComplaint(complaint);
    }
    else {
      addComplaint(complaint);
    }
  };

  const handleFormChange = (updatedObject) => {
    setNewComplaint({ ...newComplaint, ...updatedObject });
  };

  const onCancel = () => {
    props.onCancel();
    resetForm();
  };

  const getDialogActions = () => {
    let actions = [];
    actions.push(<Button key="submit" disabled={isAddingAttachments || isSubmitting || (newComplaintAttachments && newComplaintAttachments.length > 10)} onClick={() => submit(submitComplaint)}>Submit</Button>);
    actions.push(<Button key="cancel" variant="text" color="secondary" onClick={onCancel}>Cancel</Button>);
    return actions;
  };

  const onAttachmentBeginLoad = () => {
    setIsAddingAttachments(true);
  };

  const onAttachmentAdd = (attachment: Attachment, filename: string) => {
    setNewComplaintAttachments(newComplaintAttachments => [...newComplaintAttachments, { attachmentData: attachment, fileName: filename }]);
    setIsAddingAttachments(false);
  };

  const onAttachmentDelete = (filesToDelete: File[]) => {
    setNewComplaintAttachments(newComplaintAttachments => newComplaintAttachments.filter((complaint) => filesToDelete.find((file) => file.name !== complaint.fileName)));
  };

  return (
    <MDialog
      visible={props.visible}
      id="add-complaint-dialog"
      className="complaint-dialog"
      title={props.employeeName ? `Add Feedback for ${props.employeeName}` : "Add feedback"}
      width={800}
      contentActions={getDialogActions()}
    >
      {isFetchingCustomers || isFetchingCategories ? <MSpinner /> :
        <div className="complaint-add-dialog-form">
          <MGridForm
            formDefinition={memoizedFormDef}
            onChange={handleFormChange}
            data={newComplaint}
            getControlMethods={cm => { formControlMethods.current = cm; }}
          />
          <div id="complaint-add-dialog-file-manager">
            <EmployeeComplaintFileManager canAddAttachments={true} onAttachmentAdd={onAttachmentAdd} onAttachmentBeginLoad={onAttachmentBeginLoad} onAttachmentDelete={onAttachmentDelete} canDeleteFiles={true} canDownloadFiles={false} />
          </div>
          {newComplaintAttachments && newComplaintAttachments.length > 0 && newComplaintAttachments.length < 10 ?
          <div className="complaint-add-dialog-file-message" id="complaint-add-dialog-file-message-instructions">
            Click in the file manager to add more files, up to 10
          </div>
          :
          <></>
          }
          {newComplaintAttachments && newComplaintAttachments.length > 10 ? 
          <div className="complaint-add-dialog-file-message" id="complaint-add-dialog-file-message-too-many-attachments">
            Please reduce the amount of files to 10 or less
          </div>
          :
          <></>  
          }
        </div>
      }
    </MDialog>
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  }
});

export default connect(null, mapDispatchToProps)(EmployeeComplaintAddDialog);